import React, { useState, useEffect } from 'react';
import { useAuth } from '../../context/AuthContext';
import API from '../../utils/api';
import toast from 'react-hot-toast';
import { format } from 'date-fns';
import { es } from 'date-fns/locale';
import * as XLSX from 'xlsx';

const COP = (n) =>
  new Intl.NumberFormat('es-CO', { style: 'currency', currency: 'COP', maximumFractionDigits: 0 })
    .format(Number(n) || 0);
const num = (v) => parseFloat(v) || 0;

const MESES = ['Enero','Febrero','Marzo','Abril','Mayo','Junio','Julio','Agosto','Septiembre','Octubre','Noviembre','Diciembre'];

// ─────────────────────────────────────────
// EXPORTAR EXCEL
// ─────────────────────────────────────────
async function exportarExcel(cierres, sobres, mes, anio) {

  const wb = XLSX.utils.book_new();

  // Hoja 1: Cierres por día
  const cierresData = [
    ['Fecha', 'Punto de Venta', 'Ef. Aliaddo', 'Tarj. Aliaddo', 'Transf. Aliaddo', 'Rappi', 'Bonos Aliaddo',
     'Ef. Emergencia', 'Tarj. Emergencia', 'Transf. Emergencia', 'Bonos Emergencia',
     'Total Efectivo', 'Total Banco', 'Total Rappi', 'Total Bonos',
     'Compras', 'Otras Salidas', 'Efectivo Real', 'Diferencia', 'Estado'],
    ...cierres.map(c => [
      format(new Date(c.fecha), 'dd/MM/yyyy'),
      c.bodega,
      num(c.aliaddo_efectivo), num(c.aliaddo_tarjeta), num(c.aliaddo_transferencia), num(c.aliaddo_rappi), num(c.aliaddo_bonos),
      num(c.emergencia_efectivo), num(c.emergencia_tarjeta), num(c.emergencia_transferencia), num(c.emergencia_bonos),
      num(c.total_efectivo), num(c.total_banco), num(c.total_rappi),
      num(c.aliaddo_bonos) + num(c.emergencia_bonos),
      num(c.compras), num(c.otras_salidas), num(c.total_real), num(c.diferencia), c.estado,
    ])
  ];
  const ws1 = XLSX.utils.aoa_to_sheet(cierresData);
  ws1['!cols'] = Array(20).fill({ wch: 16 });
  XLSX.utils.book_append_sheet(wb, ws1, 'Cierres del Mes');

  // Hoja 2: Resumen por bodega
  const bodegas = [...new Set(cierres.map(c => c.bodega))];
  const resumenData = [
    ['Punto de Venta', 'Total Efectivo', 'Total Banco', 'Total Rappi', 'Total Bonos', 'Faltantes', 'Sobrantes', 'Días registrados'],
    ...bodegas.map(bodega => {
      const rows = cierres.filter(c => c.bodega === bodega);
      return [
        bodega,
        rows.reduce((a, c) => a + num(c.total_efectivo), 0),
        rows.reduce((a, c) => a + num(c.total_banco), 0),
        rows.reduce((a, c) => a + num(c.total_rappi), 0),
        rows.reduce((a, c) => a + num(c.aliaddo_bonos) + num(c.emergencia_bonos), 0),
        rows.filter(c => num(c.diferencia) < -100).reduce((a, c) => a + num(c.diferencia), 0),
        rows.filter(c => num(c.diferencia) > 100).reduce((a, c) => a + num(c.diferencia), 0),
        rows.length,
      ];
    })
  ];
  const ws2 = XLSX.utils.aoa_to_sheet(resumenData);
  ws2['!cols'] = Array(8).fill({ wch: 20 });
  XLSX.utils.book_append_sheet(wb, ws2, 'Resumen por Punto');

  // Hoja 3: Sobres
  const sobresData = [
    ['Fecha', 'Punto de Venta', 'Efectivo del Sobre', 'Destino', 'Cuenta', 'Comprobante / Quién retiró', 'Registrado por', 'Fecha registro'],
    ...sobres.map(s => [
      format(new Date(s.fecha), 'dd/MM/yyyy'),
      s.bodega,
      num(s.total_efectivo),
      s.destino === 'consignacion' ? 'Consignación' : s.destino === 'retiro_gerencia' ? 'Retiro Gerencia' : 'Pendiente',
      s.cuenta_destino || '—',
      s.comprobante || s.retirado_por || '—',
      s.registrado_por || '—',
      s.registrado_at ? format(new Date(s.registrado_at), 'dd/MM/yyyy HH:mm') : '—',
    ])
  ];
  const ws3 = XLSX.utils.aoa_to_sheet(sobresData);
  ws3['!cols'] = Array(8).fill({ wch: 20 });
  XLSX.utils.book_append_sheet(wb, ws3, 'Sobres');

  XLSX.writeFile(wb, `Reporte_Gelatus_${MESES[mes-1]}_${anio}.xlsx`);
  toast.success('Excel exportado correctamente');
}

// ─────────────────────────────────────────
// EXPORTAR PDF
// ─────────────────────────────────────────
async function exportarPDF(cierres, sobres, mes, anio) {
  const script1 = document.createElement('script');
  script1.src = 'https://cdnjs.cloudflare.com/ajax/libs/jspdf/2.5.1/jspdf.umd.min.js';
  document.head.appendChild(script1);
  await new Promise(r => script1.onload = r);

  const script2 = document.createElement('script');
  script2.src = 'https://cdnjs.cloudflare.com/ajax/libs/jspdf-autotable/3.8.2/jspdf.plugin.autotable.min.js';
  document.head.appendChild(script2);
  await new Promise(r => script2.onload = r);

  const { jsPDF } = window.jspdf;
  const doc = new jsPDF({ orientation: 'landscape', unit: 'mm', format: 'a4' });

  const titulo = `Reporte Gelatus — ${MESES[mes-1]} ${anio}`;
  doc.setFontSize(16);
  doc.setFont('helvetica', 'bold');
  doc.text(titulo, 14, 16);
  doc.setFontSize(10);
  doc.setFont('helvetica', 'normal');
  doc.text(`Generado el ${format(new Date(), "d 'de' MMMM yyyy", { locale: es })}`, 14, 23);

  // Tabla resumen por bodega
  const bodegas = [...new Set(cierres.map(c => c.bodega))];
  doc.setFontSize(12);
  doc.setFont('helvetica', 'bold');
  doc.text('Resumen por Punto de Venta', 14, 32);

  doc.autoTable({
    startY: 36,
    head: [['Punto de Venta', 'Efectivo', 'Banco', 'Rappi', 'Bonos', 'Faltantes', 'Sobrantes', 'Días']],
    body: bodegas.map(bodega => {
      const rows = cierres.filter(c => c.bodega === bodega);
      return [
        bodega,
        COP(rows.reduce((a, c) => a + num(c.total_efectivo), 0)),
        COP(rows.reduce((a, c) => a + num(c.total_banco), 0)),
        COP(rows.reduce((a, c) => a + num(c.total_rappi), 0)),
        COP(rows.reduce((a, c) => a + num(c.aliaddo_bonos) + num(c.emergencia_bonos), 0)),
        COP(rows.filter(c => num(c.diferencia) < -100).reduce((a, c) => a + num(c.diferencia), 0)),
        COP(rows.filter(c => num(c.diferencia) > 100).reduce((a, c) => a + num(c.diferencia), 0)),
        rows.length,
      ];
    }),
    styles: { fontSize: 9 },
    headStyles: { fillColor: [26, 26, 46], textColor: 255 },
    alternateRowStyles: { fillColor: [245, 245, 245] },
  });

  // Tabla cierres del mes
  doc.addPage();
  doc.setFontSize(12);
  doc.setFont('helvetica', 'bold');
  doc.text('Cierres del Mes', 14, 16);

  doc.autoTable({
    startY: 20,
    head: [['Fecha', 'Punto', 'Efectivo', 'Banco', 'Rappi', 'Bonos', 'Ef. Real', 'Diferencia', 'Estado']],
    body: cierres.map(c => [
      format(new Date(c.fecha), 'dd/MM'),
      c.bodega,
      COP(c.total_efectivo),
      COP(c.total_banco),
      COP(c.total_rappi),
      COP(num(c.aliaddo_bonos) + num(c.emergencia_bonos)),
      COP(c.total_real),
      COP(c.diferencia),
      c.estado,
    ]),
    styles: { fontSize: 8 },
    headStyles: { fillColor: [26, 26, 46], textColor: 255 },
    alternateRowStyles: { fillColor: [245, 245, 245] },
    didParseCell: (data) => {
      if (data.section === 'body' && data.column.index === 7) {
        const val = num(cierres[data.row.index]?.diferencia);
        if (val < -100) data.cell.styles.textColor = [239, 68, 68];
        else if (val > 100) data.cell.styles.textColor = [245, 158, 11];
        else data.cell.styles.textColor = [16, 185, 129];
      }
    }
  });

  // Tabla sobres
  doc.addPage();
  doc.setFontSize(12);
  doc.setFont('helvetica', 'bold');
  doc.text('Sobres — Trazabilidad de Efectivo', 14, 16);

  doc.autoTable({
    startY: 20,
    head: [['Fecha', 'Punto', 'Efectivo', 'Destino', 'Comprobante / Quién retiró', 'Registrado por']],
    body: sobres.map(s => [
      format(new Date(s.fecha), 'dd/MM'),
      s.bodega,
      COP(s.total_efectivo),
      s.destino === 'consignacion' ? 'Consignación' : s.destino === 'retiro_gerencia' ? 'Retiro Gerencia' : '⏳ Pendiente',
      s.comprobante || s.retirado_por || '—',
      s.registrado_por || '—',
    ]),
    styles: { fontSize: 8 },
    headStyles: { fillColor: [26, 26, 46], textColor: 255 },
    alternateRowStyles: { fillColor: [245, 245, 245] },
  });

  doc.save(`Reporte_Gelatus_${MESES[mes-1]}_${anio}.pdf`);
  toast.success('PDF exportado correctamente');
}

// ─────────────────────────────────────────
// TARJETA RESUMEN BODEGA
// ─────────────────────────────────────────
function TarjetaBodega({ bodega, cierres }) {
  const rows = cierres.filter(c => c.bodega === bodega);
  const totalEfectivo = rows.reduce((a, c) => a + num(c.total_efectivo), 0);
  const totalBanco    = rows.reduce((a, c) => a + num(c.total_banco), 0);
  const totalRappi    = rows.reduce((a, c) => a + num(c.total_rappi), 0);
  const totalBonos    = rows.reduce((a, c) => a + num(c.aliaddo_bonos) + num(c.emergencia_bonos), 0);
  const faltantes     = rows.filter(c => num(c.diferencia) < -100).reduce((a, c) => a + num(c.diferencia), 0);
  const sobrantes     = rows.filter(c => num(c.diferencia) > 100).reduce((a, c) => a + num(c.diferencia), 0);
  const total         = totalEfectivo + totalBanco + totalRappi + totalBonos;

  return (
    <div style={{ background: 'white', borderRadius: 14, overflow: 'hidden', boxShadow: '0 1px 4px rgba(0,0,0,0.08)', marginBottom: 16 }}>
      <div style={{ background: '#1a1a2e', color: 'white', padding: '12px 18px', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <span style={{ fontWeight: 800, fontSize: 15 }}>🏪 {bodega}</span>
        <span style={{ fontFamily: 'monospace', fontSize: 16, fontWeight: 700 }}>{COP(total)}</span>
      </div>
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(150px, 1fr))', gap: 12, padding: 16 }}>
        <Metric label="💵 Efectivo" valor={totalEfectivo} color="#10B981" />
        <Metric label="🏦 Banco" valor={totalBanco} color="#3B82F6" />
        <Metric label="🛵 Rappi" valor={totalRappi} color="#F97316" />
        <Metric label="🎟️ Bonos" valor={totalBonos} color="#8B5CF6" />
        {faltantes < 0 && <Metric label="❌ Faltantes" valor={faltantes} color="#EF4444" />}
        {sobrantes > 0 && <Metric label="⚠️ Sobrantes" valor={sobrantes} color="#F59E0B" />}
        <Metric label="📅 Días" valor={rows.length} color="#6B7280" isCOP={false} />
      </div>
    </div>
  );
}

function Metric({ label, valor, color, isCOP = true }) {
  return (
    <div style={{ background: '#F9FAFB', borderRadius: 8, padding: '10px 12px' }}>
      <div style={{ fontSize: 11, color: '#6B7280', fontWeight: 700, marginBottom: 4 }}>{label}</div>
      <div style={{ fontSize: 17, fontWeight: 800, color, fontFamily: 'monospace' }}>
        {isCOP ? COP(valor) : valor}
      </div>
    </div>
  );
}

// ─────────────────────────────────────────
// TABLA CIERRES
// ─────────────────────────────────────────
function TablaCierres({ cierres }) {
  if (!cierres.length) return null;
  return (
    <div style={{ background: 'white', borderRadius: 14, overflow: 'hidden', boxShadow: '0 1px 4px rgba(0,0,0,0.08)', marginBottom: 16 }}>
      <div style={{ padding: '14px 18px', borderBottom: '1px solid #F3F4F6', fontWeight: 800, fontSize: 15 }}>📋 Historial de Cierres</div>
      <div style={{ overflowX: 'auto' }}>
        <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 12 }}>
          <thead>
            <tr style={{ background: '#F9FAFB' }}>
              {['Fecha','Punto','Efectivo','Banco','Rappi','Bonos','Ef. Real','Diferencia','Estado'].map(h => (
                <th key={h} style={{ padding: '9px 12px', textAlign: 'left', fontSize: 10, fontWeight: 700, color: '#6B7280', textTransform: 'uppercase', whiteSpace: 'nowrap' }}>{h}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {cierres.map((c, i) => {
              const dif = num(c.diferencia);
              const cuadra = Math.abs(dif) < 100;
              return (
                <tr key={i} style={{ borderTop: '1px solid #F3F4F6', background: i % 2 === 0 ? 'white' : '#FAFAFA' }}>
                  <td style={{ padding: '9px 12px', fontWeight: 600, whiteSpace: 'nowrap' }}>
                    {format(new Date(c.fecha), 'd MMM', { locale: es })}
                  </td>
                  <td style={{ padding: '9px 12px', fontSize: 11 }}>{c.bodega}</td>
                  <td style={{ padding: '9px 12px', fontFamily: 'monospace', color: '#10B981', fontWeight: 700 }}>{COP(c.total_efectivo)}</td>
                  <td style={{ padding: '9px 12px', fontFamily: 'monospace', color: '#3B82F6', fontWeight: 700 }}>{COP(c.total_banco)}</td>
                  <td style={{ padding: '9px 12px', fontFamily: 'monospace', color: '#F97316', fontWeight: 700 }}>{COP(c.total_rappi)}</td>
                  <td style={{ padding: '9px 12px', fontFamily: 'monospace', color: '#8B5CF6', fontWeight: 700 }}>{COP(num(c.aliaddo_bonos) + num(c.emergencia_bonos))}</td>
                  <td style={{ padding: '9px 12px', fontFamily: 'monospace', fontWeight: 600 }}>{COP(c.total_real)}</td>
                  <td style={{ padding: '9px 12px', fontFamily: 'monospace', fontWeight: 700, color: cuadra ? '#10B981' : dif < 0 ? '#EF4444' : '#F59E0B' }}>
                    {dif >= 0 ? '+' : ''}{COP(dif)}
                  </td>
                  <td style={{ padding: '9px 12px' }}>
                    <span style={{ padding: '2px 8px', borderRadius: 20, fontSize: 10, fontWeight: 700, background: { aprobado: '#D1FAE5', enviado: '#DBEAFE', borrador: '#F3F4F6', con_novedad: '#FEE2E2' }[c.estado] || '#F3F4F6', color: { aprobado: '#065F46', enviado: '#1E40AF', borrador: '#6B7280', con_novedad: '#991B1B' }[c.estado] || '#6B7280' }}>
                      {c.estado}
                    </span>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
}

// ─────────────────────────────────────────
// TABLA SOBRES
// ─────────────────────────────────────────
function TablaSobres({ sobres }) {
  if (!sobres.length) return null;
  const pendientes = sobres.filter(s => !s.destino);
  const consignaciones = sobres.filter(s => s.destino === 'consignacion');
  const retiros = sobres.filter(s => s.destino === 'retiro_gerencia');

  return (
    <div style={{ background: 'white', borderRadius: 14, overflow: 'hidden', boxShadow: '0 1px 4px rgba(0,0,0,0.08)', marginBottom: 16 }}>
      <div style={{ padding: '14px 18px', borderBottom: '1px solid #F3F4F6', display: 'flex', justifyContent: 'space-between', flexWrap: 'wrap', gap: 10 }}>
        <span style={{ fontWeight: 800, fontSize: 15 }}>💵 Sobres del Mes</span>
        <div style={{ display: 'flex', gap: 10, fontSize: 12 }}>
          <span style={{ background: '#D1FAE5', color: '#065F46', padding: '3px 10px', borderRadius: 20, fontWeight: 700 }}>🏦 {consignaciones.length} consignaciones</span>
          <span style={{ background: '#EEF2FF', color: '#4338CA', padding: '3px 10px', borderRadius: 20, fontWeight: 700 }}>🤝 {retiros.length} retiros</span>
          {pendientes.length > 0 && <span style={{ background: '#FEF3C7', color: '#92400E', padding: '3px 10px', borderRadius: 20, fontWeight: 700 }}>⏳ {pendientes.length} pendientes</span>}
        </div>
      </div>
      <div style={{ overflowX: 'auto' }}>
        <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 12 }}>
          <thead>
            <tr style={{ background: '#F9FAFB' }}>
              {['Fecha','Punto','Efectivo','Destino','Cuenta / Quién retiró','Comprobante','Registrado por'].map(h => (
                <th key={h} style={{ padding: '9px 12px', textAlign: 'left', fontSize: 10, fontWeight: 700, color: '#6B7280', textTransform: 'uppercase', whiteSpace: 'nowrap' }}>{h}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {sobres.map((s, i) => (
              <tr key={i} style={{ borderTop: '1px solid #F3F4F6', background: i % 2 === 0 ? 'white' : '#FAFAFA' }}>
                <td style={{ padding: '9px 12px', fontWeight: 600, whiteSpace: 'nowrap' }}>
                  {format(new Date(s.fecha), 'd MMM', { locale: es })}
                </td>
                <td style={{ padding: '9px 12px', fontSize: 11 }}>{s.bodega}</td>
                <td style={{ padding: '9px 12px', fontFamily: 'monospace', color: '#10B981', fontWeight: 700 }}>{COP(s.total_efectivo)}</td>
                <td style={{ padding: '9px 12px' }}>
                  {s.destino === 'consignacion' ? <span style={{ color: '#3B82F6', fontWeight: 600 }}>🏦 Consignación</span>
                  : s.destino === 'retiro_gerencia' ? <span style={{ color: '#6366F1', fontWeight: 600 }}>🤝 Retiro Gerencia</span>
                  : <span style={{ color: '#F59E0B', fontWeight: 600 }}>⏳ Pendiente</span>}
                </td>
                <td style={{ padding: '9px 12px', fontSize: 11 }}>{s.cuenta_destino || s.retirado_por || '—'}</td>
                <td style={{ padding: '9px 12px', fontSize: 11 }}>{s.comprobante || '—'}</td>
                <td style={{ padding: '9px 12px', fontSize: 11 }}>{s.registrado_por || '—'}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

// ─────────────────────────────────────────
// MÓDULO PRINCIPAL
// ─────────────────────────────────────────
export default function Reportes() {
  const { puedeVer } = useAuth();
  const esGerencia = puedeVer('gerencia');
  const hoy = new Date();

  const [mes, setMes]         = useState(hoy.getMonth() + 1);
  const [anio, setAnio]       = useState(hoy.getFullYear());
  const [cierres, setCierres] = useState([]);
  const [sobres, setSobres]   = useState([]);
  const [cargando, setCargando] = useState(false);
  const [exportandoExcel, setExportandoExcel] = useState(false);
  const [exportandoPDF, setExportandoPDF]     = useState(false);
  const [tab, setTab]         = useState('resumen');

  const cargar = async () => {
    setCargando(true);
    try {
      const [r1, r2] = await Promise.all([
        API.get('/reportes/cierres', { params: { mes, anio } }),
        API.get('/reportes/sobres',  { params: { mes, anio } }),
      ]);
      setCierres(r1.data);
      setSobres(r2.data);
    } catch { toast.error('Error cargando datos'); }
    finally { setCargando(false); }
  };

  useEffect(() => { cargar(); }, [mes, anio]);

  const bodegas = [...new Set(cierres.map(c => c.bodega))];
  const totalGeneral = cierres.reduce((a, c) => a + num(c.total_efectivo) + num(c.total_banco) + num(c.total_rappi) + num(c.aliaddo_bonos) + num(c.emergencia_bonos), 0);

  const handleExcel = async () => {
    setExportandoExcel(true);
    try { await exportarExcel(cierres, sobres, mes, anio); }
    catch { toast.error('Error exportando Excel'); }
    finally { setExportandoExcel(false); }
  };

  const handlePDF = async () => {
    setExportandoPDF(true);
    try { await exportarPDF(cierres, sobres, mes, anio); }
    catch { toast.error('Error exportando PDF'); }
    finally { setExportandoPDF(false); }
  };

  return (
    <div style={{ fontFamily: "'Segoe UI', sans-serif", maxWidth: 1100, margin: '0 auto' }}>
      {/* Header */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 20, flexWrap: 'wrap', gap: 12 }}>
        <div>
          <h1 style={{ margin: 0, fontSize: 26, fontWeight: 800, color: '#111827' }}>📈 Reportes</h1>
          <p style={{ margin: '4px 0 0', color: '#6B7280', fontSize: 14 }}>Consolidado mensual por punto de venta</p>
        </div>
        <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
          <button onClick={handleExcel} disabled={exportandoExcel || !cierres.length} style={{ padding: '9px 16px', border: '2px solid #10B981', borderRadius: 10, background: 'white', color: '#065F46', cursor: 'pointer', fontSize: 13, fontWeight: 700 }}>
            {exportandoExcel ? '⏳...' : '📊 Excel'}
          </button>
          <button onClick={handlePDF} disabled={exportandoPDF || !cierres.length} style={{ padding: '9px 16px', border: '2px solid #EF4444', borderRadius: 10, background: 'white', color: '#991B1B', cursor: 'pointer', fontSize: 13, fontWeight: 700 }}>
            {exportandoPDF ? '⏳...' : '📄 PDF'}
          </button>
        </div>
      </div>

      {/* Filtros */}
      <div style={{ display: 'flex', gap: 12, marginBottom: 20, flexWrap: 'wrap', alignItems: 'flex-end' }}>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
          <label style={{ fontSize: 11, fontWeight: 700, color: '#9CA3AF', textTransform: 'uppercase', letterSpacing: '0.06em' }}>Mes</label>
          <select value={mes} onChange={e => setMes(Number(e.target.value))} style={{ padding: '9px 14px', border: '2px solid #E5E7EB', borderRadius: 10, fontSize: 14, outline: 'none', fontWeight: 600, background: 'white' }}>
            {MESES.map((m, i) => <option key={i} value={i + 1}>{m}</option>)}
          </select>
        </div>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
          <label style={{ fontSize: 11, fontWeight: 700, color: '#9CA3AF', textTransform: 'uppercase', letterSpacing: '0.06em' }}>Año</label>
          <select value={anio} onChange={e => setAnio(Number(e.target.value))} style={{ padding: '9px 14px', border: '2px solid #E5E7EB', borderRadius: 10, fontSize: 14, outline: 'none', fontWeight: 600, background: 'white' }}>
            {[2025, 2026, 2027].map(a => <option key={a} value={a}>{a}</option>)}
          </select>
        </div>
        {cierres.length > 0 && (
          <div style={{ padding: '9px 18px', background: '#1a1a2e', color: 'white', borderRadius: 10, fontSize: 14, fontWeight: 700, fontFamily: 'monospace' }}>
            Total: {COP(totalGeneral)}
          </div>
        )}
      </div>

      {/* Tabs */}
      <div style={{ display: 'flex', gap: 4, marginBottom: 20, background: 'white', borderRadius: 12, padding: 4, boxShadow: '0 1px 4px rgba(0,0,0,0.07)', width: 'fit-content' }}>
        {[
          { id: 'resumen', label: '📊 Resumen' },
          { id: 'cierres', label: '💰 Cierres' },
          { id: 'sobres',  label: '💵 Sobres' },
        ].map(t => (
          <button key={t.id} onClick={() => setTab(t.id)} style={{ padding: '8px 18px', border: 'none', borderRadius: 8, background: tab === t.id ? '#1a1a2e' : 'transparent', color: tab === t.id ? 'white' : '#6B7280', cursor: 'pointer', fontSize: 13, fontWeight: tab === t.id ? 700 : 500, transition: 'all 0.15s' }}>
            {t.label}
          </button>
        ))}
      </div>

      {cargando ? (
        <div style={{ padding: 40, textAlign: 'center', color: '#9CA3AF' }}>Cargando datos...</div>
      ) : cierres.length === 0 ? (
        <div style={{ background: 'white', borderRadius: 14, padding: 40, textAlign: 'center', color: '#9CA3AF', boxShadow: '0 1px 4px rgba(0,0,0,0.07)' }}>
          <div style={{ fontSize: 40, marginBottom: 12 }}>📭</div>
          <div style={{ fontSize: 16, fontWeight: 600 }}>Sin datos para {MESES[mes - 1]} {anio}</div>
        </div>
      ) : (
        <>
          {tab === 'resumen' && bodegas.map(b => <TarjetaBodega key={b} bodega={b} cierres={cierres} />)}
          {tab === 'cierres' && <TablaCierres cierres={cierres} />}
          {tab === 'sobres'  && <TablaSobres sobres={sobres} />}
        </>
      )}
    </div>
  );
}
